<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import TipTapEditor2 from "@/features/document-editor/ui/TipTapEditor2.vue";
import PreviewTipTap2 from "@/features/document-editor/ui/PreviewTipTap2.vue";
import ServiceSection from "@/pages/admin/ConstructorActions/tabs/components/ServiceSection.vue";
import {
  getServiceListWithResponse,
  getServiceList,
  getServiceListChains,
  getServiceChainsStepList,
  MANUAL_SERVICES,
  getManualService,
  uploadDocxToTiptap,
} from "@/entities/template";
import { showAlert } from "@/shared/store/alertStore";
import { useConfirm } from "@/shared/ui/alert-dialog/useConfirm";
import { getSchemeNotaries } from "@/entities/session";
import AddCustomFields from "@/shared/ui/custom-fields/AddCustomFields.vue";
import { ParticipantDropdown } from "@/features/participant-dropdown";
import { useParticipantTypes } from "@/entities/directory";
import Button from "../../../../shared/ui/button/Button.vue";

const route = useRoute();
const { t } = useI18n();
const confirm = useConfirm();

const props = defineProps({
  form: {
    type: Object,
    default: () => ({}),
  },
});

const { participantTypes, fetchParticipantTypes } = useParticipantTypes();

// refs for template elements
const editorRef = ref(null);
const participantDropdownRef = ref(null);
const previewEditorRef = ref(null);

// data
const serviceListWithResponse = ref([]);
const serviceList = ref([]);
const serviceChainSteps = ref([]);
const notarySchema = ref(null);
const loadingService = ref(false);
const activeService = ref(null);
const selectedService = ref(null);
const templateLang = ref("ru");

const visibleCustomField = ref(false);
const editingFieldKey = ref(null);

const extractedMentions = ref([]);
// computed
const titleKey = computed(() => {
  return (
    {
      kz: "content_kz",
      ru: "content",
      en: "content_en",
    }[templateLang.value] || "content"
  );
});

const isBothLanguages = computed(() => {
  return props.form.multiple_languages;
});

const isCustomField = computed(() => {
  return ["all_fields", "favorite_fields"].includes(activeService.value);
});

const templateView = computed(() => {
  return route.name === "constructor_action_item_view";
});

const templateJson = computed(() => {
  const json = props.form[titleKey.value];
  return json ? JSON.parse(JSON.stringify(json)) : null;
});

const mentionParticipantNames = computed(() =>
  Object.fromEntries(
    (participantTypes.value || []).map((p) => [
      p.code,
      (templateLang.value === "kz" ? p.name_kz || p.name : p.name) || p.code,
    ])
  )
);

const customFields = computed(() => {
  return Object.fromEntries(
    Object.entries(props.form?.fields || {}).map(([key, field]) => [
      key,
      {
        ...field,
        key: key,
        code: key,
      },
    ])
  );
});

const selectedExternalServices = computed(() => {
  const result = [];
  serviceChainSteps.value.forEach((step) => {
    const serviceRow = serviceList.value.find((s) => s.id === step.service_id);
    if (!serviceRow) return;
    const service = serviceListWithResponse.value.find(
      (s) => s.code === serviceRow.code
    );
    if (!service) return;

    const mapping = step.response_mapping || {};
    const sourceProperties = service.response?.properties || {};
    const filteredProperties = {};
    Object.entries(sourceProperties).forEach(([key, prop]) => {
      if (mapping[key]) {
        filteredProperties[key] = prop;
      }
    });

    result.push({
      ...service,
      response: { ...service.response, properties: filteredProperties },
    });
  });
  return result;
});

const serviceGroups = computed(() => {
  const items = selectedExternalServices.value.map((service) => ({
    name: service.name,
    code: service.code,
  }));

  return [
    {
      title: t("Кастомные атрибуты"),
      key: "custom_atribut",
      expanded: true,
      items: [
        { name: t("Все поля"), code: "all_fields" },
        { name: t("Избранные"), code: "favorite_fields" },
        ...MANUAL_SERVICES.map((s) => ({ name: t(s.name), code: s.code })),
      ],
    },
    {
      title: t("Внешние сервисы"),
      key: "external_services",
      expanded: true,
      items,
    },
    {
      title: t("Внутренние сервисы"),
      key: "internal_services",
      expanded: true,
      items: [
        { name: t("Данные о нотариусе"), code: "notary" },
        { name: t("Подпись"), code: "signature" },
      ],
    },
  ];
});

// methods
function handleSave(json) {
  props.form[titleKey.value] = json;
  if (isBothLanguages.value) {
    props.form.content = json;
    props.form.content_kz = json;
  }
  extractMentionsFromDocument();
}

async function loadDocxFile(file) {
  if (!file) return;

  const current = editorRef.value?.getDocument();
  const hasContent = (current?.content || []).some(
    (node) => node.content?.length
  );
  if (hasContent) {
    try {
      await confirm(
        t(
          "Загрузка заменит текущее содержимое шаблона для выбранного языка. Продолжить?"
        ),
        t("Загрузить шаблон"),
        {
          confirmButtonText: t("Загрузить"),
          cancelButtonText: t("Отмена"),
          type: "warning",
        }
      );
    } catch {
      return;
    }
  }

  try {
    const res = await uploadDocxToTiptap(file);
    const doc = res?.data;

    if (!doc || doc.type !== "doc") {
      showAlert(
        t("Ошибка"),
        res?.error || t("Не удалось обработать файл"),
        "error"
      );
      return;
    }

    props.form[titleKey.value] = doc;
    if (isBothLanguages.value) {
      props.form.content = doc;
      props.form.content_kz = doc;
    }
    showAlert(t("Шаблон загружен"), "", "success");
  } catch (e) {
    showAlert(
      t("Ошибка"),
      e?.response?.data?.error || e?.message || t("Не удалось загрузить файл"),
      "error"
    );
  }
}

function toggleBothLanguages(val) {
  props.form.multiple_languages = val;
  if (val) {
    const currentJson = editorRef.value?.getDocument();
    if (currentJson) {
      props.form.content = currentJson;
      props.form.content_kz = currentJson;
    }
  }
}

async function fetchNotarySchema() {
  try {
    const response = await getSchemeNotaries();
    if (response?.success && response.payload) {
      notarySchema.value = response.payload;
    }
  } catch (e) {
    console.error("fetchNotarySchema error:", e);
  }
}

async function getServiceListWithResponseFn() {
  loadingService.value = true;
  try {
    const list = await getServiceListWithResponse();
    if (list.success) {
      serviceListWithResponse.value = list.payload || [];
    }

    const pureList = await getServiceList();
    if (pureList.success) {
      serviceList.value = pureList.payload || [];
    }

    await loadServiceChainSteps();
  } finally {
    loadingService.value = false;
  }
}

async function loadServiceChainSteps() {
  const uuid = route.params.id;
  if (!uuid) return;

  const chainData = await getServiceListChains({ code: uuid });
  if (!chainData.success || !chainData.payload?.length) {
    serviceChainSteps.value = [];
    return;
  }

  const stepsData = await getServiceChainsStepList(chainData.payload[0].id);
  if (stepsData.success) {
    serviceChainSteps.value = stepsData.payload || [];
  }
}

async function validate() {
  const forms = [].filter(Boolean);
  const results = await Promise.all(
    forms.map((f) => new Promise((resolve) => f.validate((v) => resolve(v))))
  );
  return results.every(Boolean);
}

function changeService(service) {
  if (service && service.code) {
    activeService.value = service.code;
  }

  const code = activeService.value;

  if (code === "all_fields") {
    selectedService.value = {
      name: t("Кастомные поля"),
      code: "all_fields",
      response: {
        properties: mapFieldsToServiceResponse(props.form.fields),
      },
    };
    return;
  }

  if (code === "favorite_fields") {
    const favFields = Object.fromEntries(
      Object.entries(props.form.fields || {}).filter(
        ([, field]) => field.favourites === true
      )
    );

    selectedService.value = {
      name: t("Избранные"),
      code: "favorite_fields",
      response: {
        properties: mapFieldsToServiceResponse(favFields),
      },
    };
    return;
  }

  if (code === "signature") {
    const selectedCodes = [
      ...(props.form?.participant_fl_type_codes || []),
      ...(props.form?.participant_ul_type_codes || []),
    ];
    const filtered = participantTypes.value.filter((pt) =>
      selectedCodes.includes(pt.code)
    );
    const properties = {};
    filtered.forEach((pt) => {
      properties[pt.code] = {
        name: pt.name || pt.title,
        key: pt.code,
        type: "signature",
        participant_type_code: pt.code,
        participant_type: "participant",
        editable: false,
        isCustom: false,
      };
    });
    properties.notary_signature = {
      name: t("Подпись нотариуса"),
      key: "notary_signature",
      type: "signature",
      participant_type_code: "notary",
      participant_type: "notary",
      editable: false,
      isCustom: false,
    };
    selectedService.value = {
      name: t("Подпись"),
      code: "signature",
      response: { properties },
    };
    return;
  }

  if (code === "notary") {
    const raw = notarySchema.value?.properties || {};
    const properties = {};
    Object.entries(raw).forEach(([key, field]) => {
      properties[key] = {
        ...field,
        key,
        isNotary: true,
        isCustom: false,
        editable: false,
      };
    });
    selectedService.value = {
      name: t("Данные о нотариусе"),
      code: "notary",
      response: { properties },
    };
    return;
  }

  const manualService = getManualService(code);
  if (manualService) {
    selectedService.value = {
      name: manualService.name,
      code: manualService.code,
      response: manualService.response,
    };
    return;
  }

  const found = selectedExternalServices.value.find((s) => s.code === code);

  selectedService.value = found
    ? {
        ...found,
        response: {
          ...found.response,
        },
      }
    : null;
}

function mapFieldsToServiceResponse(fields) {
  if (!fields) return {};

  const result = {};

  Object.entries(fields).forEach(([code, field]) => {
    result[code] = {
      name: field.name || field.name_kz,
      key: code,
      type: field.type || "string",
      description: field.name,
      example: field.example || "",
      nullable: field.nullable ?? true,
      enums: field.enums || [],
      editable: true,
      isCustom: true,
    };
  });

  return result;
}

function changeLang(lang) {
  templateLang.value = lang;
}

async function changeMode() {
  await previewEditorRef.value?.open({});
}

function addCustomField() {
  editingFieldKey.value = null;
  visibleCustomField.value = true;
}

function addOrUpdateField(field) {
  if (!props.form.fields) props.form.fields = {};

  const newCode = field.code;
  const oldCode = field.original_code;

  if (!newCode) return;

  if (oldCode && oldCode !== newCode) {
    const old = props.form.fields[oldCode] || {};

    delete props.form.fields[oldCode];

    props.form.fields[newCode] = {
      ...old,
      name: field.name,
      name_kz: field.name_kz,
      favourites: field.favourites ?? false,
      code: newCode,
      key: newCode,
    };
    changeService();

    return;
  }

  props.form.fields[newCode] = {
    name: field.name,
    name_kz: field.name_kz,
    value: "",
    favourites: field.favourites ?? false,
    type: field.type || "string",
    enums: field.enums || [],
    example: field.example || "",
    nullable: field.nullable ?? true,
    code: newCode,
    key: newCode,
  };
  changeService();
}

function removeField(field_key) {
  if (!props.form.fields) return;
  delete props.form.fields[field_key];
  changeService();
}

function editCustomField(field_key) {
  editingFieldKey.value = field_key;
  visibleCustomField.value = true;
}

function handleMentionsUpdate(mentions) {
  extractedMentions.value = mentions;
}

function extractMentionsFromDocument() {
  if (editorRef.value) {
    extractedMentions.value = editorRef.value.extractMentions();
  }
}

function insertFieldAsMention(field) {
  if (!editorRef.value) return;

  if (field.type === "signature") {
    editorRef.value.insertSignature({
      participant_type_code: field.participant_type_code || field.key,
      participant_name: field.name || field.title,
      participant_type: field.participant_type || "participant",
    });
    return;
  }

  if (field.isCustom) {
    editorRef.value.insertMentionByType({
      key: field.code || field.key,
      label: field.name || field.title,
      service_code: field.service_code,
      participant_type_code: field?.participant_type_code,
      isCustom: true,
      isNotary: field.isNotary || false,
    });
    return;
  }

  if (field.isNotary) {
    editorRef.value.insertMentionByType({
      key: field.code || field.key,
      label: field.name || field.title,
      service_code: field.service_code,
      participant_type_code: field?.participant_type_code,
      isCustom: false,
      isNotary: true,
    });
    return;
  }

  editorRef.value.insertMentionByKey(
    field.code || field.key,
    field.name || field.title,
    field.service_code,
    field?.participant_type_code
  );
}

function openMentionDropdown({
  participant_type_code,
  onSave,
  onDelete,
  onRestore,
  onNumberToWords,
  hasValue,
  rect,
}) {
  const selectedCodes = [
    ...(props.form?.participant_fl_type_codes || []),
    ...(props.form?.participant_ul_type_codes || []),
  ];
  participantDropdownRef.value.openDropdown({
    selectedType: participant_type_code,
    selectedCodes,
    onSave,
    onDelete,
    onRestore,
    onNumberToWords,
    hasValue,
    rect,
  });
}

// mounted
onMounted(() => {
  getServiceListWithResponseFn();
  fetchNotarySchema();
  fetchParticipantTypes();
});

defineExpose({ validate, loadDocxFile });
</script>

<template>
  <div class="template">
    <div class="block">
      <TipTapEditor2
        :mode="templateView ? 'view' : 'edit'"
        :with-border="!templateView"
        :templateLang="templateLang"
        :json="templateJson"
        :customFields="customFields"
        :serviceFields="selectedService?.response?.properties"
        :isBothLanguages="isBothLanguages"
        :default-participant-code="form?.default_participant_code"
        :mention-participant-names="mentionParticipantNames"
        @change:lang="changeLang"
        @change:mode="changeMode"
        @update:json="handleSave"
        @update:isBothLanguages="toggleBothLanguages"
        @mentions="handleMentionsUpdate"
        @edit:mention="openMentionDropdown"
        ref="editorRef"
      />
    </div>

    <div v-if="!templateView" class="block h-full">
      <div class="inner h-full flex">
        <div
          class="border border-0 border-r border-solid border-gray-200 w-1/2 h-full"
        >
          <div class="p-6 h-full overflow-y-auto">
            <ServiceSection
              :title="$t('Магические вставки')"
              :activeService="activeService"
              :selectedService="selectedService"
              :groups="serviceGroups"
              :show-action="isCustomField"
              :selected-participant-codes="[
                ...(form?.participant_fl_type_codes || []),
                ...(form?.participant_ul_type_codes || []),
              ]"
              :default-participant-code="form?.default_participant_code"
              @change:service="changeService"
              @edit:field="editCustomField"
              @insert:field="insertFieldAsMention"
              type="show-service-response"
            >
              <template #action>
                <div v-if="isCustomField" class="flex flex-col gap-6">
                  <Button
                    variant="outline"
                    class="w-full"
                    @click="addCustomField"
                  >
                    {{ $t("Добавить поле") }}
                  </Button>

                  <div
                    v-if="!form.fields || Object.keys(form.fields).length === 0"
                    class="text-gray-600 text-sm"
                  >
                    {{ $t("Кастомные поля не добавлены") }}
                  </div>
                </div>
              </template>
            </ServiceSection>
          </div>
        </div>

        <div class="w-1/2 h-full overflow-y-auto">
          <ServiceSection
            :title="$t('Сервисы')"
            :activeService="activeService"
            :selectedService="selectedService"
            :groups="serviceGroups"
            :isAccessLoading="loadingService"
            @change:service="changeService"
            @edit:field="editCustomField"
            @insert:field="insertFieldAsMention"
            class="p-6"
            type="show-service-list"
          />
        </div>
      </div>
    </div>

    <AddCustomFields
      v-if="visibleCustomField"
      :editing-key="editingFieldKey"
      :template="form"
      @close="visibleCustomField = false"
      @save="addOrUpdateField"
      @remove="removeField"
    />

    <ParticipantDropdown ref="participantDropdownRef" />

    <PreviewTipTap2
      :templateLang="templateLang"
      :templateJson="templateJson"
      :content-by-lang="{
        ru: form.content,
        kz: form.content_kz,
        en: form.content_en,
      }"
      ref="previewEditorRef"
    />
  </div>
</template>

<style scoped lang="scss">
.template {
  @apply flex h-full w-full overflow-x-hidden gap-5;

  .block {
    @apply flex flex-col w-1/2 h-full  rounded-2xl gap-6 overflow-hidden;
    background-color: hsl(var(--background));

    min-height: 600px !important;

    box-shadow: 0 1px 1px -0.5px rgba(0, 0, 0, 0.03),
      0 3px 3px -1.5px rgba(0, 0, 0, 0.03),
      0 20px 20px -12px rgba(0, 0, 0, 0.03);

    > .inner {
      @apply flex-1 overflow-auto;
    }
  }
}

@media (max-width: 1500px) {
  .template {
    @apply flex flex-col;
    .block {
      @apply w-full;
    }
  }
}
</style>
